import { useMemo, useState, type ReactNode } from 'react'
import type { TeacherSettingDefinition, TeacherSettingsResponse, SettingCategory, SettingSubgroup } from '@/types/teacher-request'
import {
  Check, X, AlertTriangle, Settings, RotateCcw, Sparkles,
  ShieldCheck, Heart, Scale, ToggleRight, Clock, Eye, Package, Calendar, Timer, Send, Link as LinkIcon
} from 'lucide-react'
import { DisabledTooltip } from '@/components/teacher-request/DisabledTooltip'
import { TagInput } from '@/components/config/TagInput'
import { FloatInput } from '@/components/ui/FloatInput'
import { GlassTooltip } from '@/components/ui/GlassTooltip'
import { CalendarFeedPreview } from '@/components/calendar/CalendarFeedPreview'
import { formatJsonListDisplay } from '@/utils/formatJsonList'

function choiceLabel(value: string): string {
  return value.replace(/_/g, ' ')
}

const MODE_ORDER_ALLOWED = new Set(['automatic', 'priority'])
const MODE_ORDER_LABELS: Record<string, string> = { automatic: 'Automatisch', priority: 'Priorität' }
const LEGACY_MODE_LABELS: Record<string, string> = { sequential: 'Sequenziell', parallel: 'Parallel', automatic: 'Automatisch' }

function displayValue(val: unknown, fallback: string = '—'): string {
  if (val === null || val === undefined) return fallback
  return String(val)
}

/** Storage keys for non-substitution_request fields are prefixed "ct:key". */
function splitKey(storageKey: string): { ct: string | null; key: string } {
  const idx = storageKey.indexOf(':')
  if (idx === -1) return { ct: null, key: storageKey }
  return { ct: storageKey.slice(0, idx), key: storageKey.slice(idx + 1) }
}

const CATEGORY_META: Record<string, { icon: typeof Settings; description: string }> = {
  'Prioritätsanfragen':      { icon: Send,        description: 'Einstellungen für manuelle Prioritätsanfragen.' },
  'Automatikmodus':          { icon: Sparkles,    description: 'Einstellungen für den automatischen Modus (Combinatorial Cascade).' },
  'Allgemeine Einstellungen': { icon: Settings,    description: 'Einstellungen die für alle Modi gelten.' },
}

const SUBGROUP_META: Record<string, { icon: typeof Settings; description: string }> = {
  'Anfrage-Limits':       { icon: Scale,       description: 'Begrenzungen für Vertretungsanfragen.' },
  'Timing':               { icon: Clock,       description: 'Standard-Ablaufzeiten und Vorlaufzeiten.' },
  'Cascade':              { icon: Timer,       description: 'Cascade-Timeout und Anzeige.' },
  'Affinity-Ranking':     { icon: Heart,       description: 'Bonuspunkte für Lehrer mit Klassen-/Fachbezug.' },
  'Cascade Preview':      { icon: Eye,         description: 'Vorschau-Einstellungen für Kaskade.' },
  'Multi-Week-Pakete':    { icon: Calendar,    description: 'Pakete über mehrere Wochen.' },
  'Anfragemodi':          { icon: ToggleRight, description: 'Automatik oder Priorität — Sequential/Parallel entscheidet die Zone.' },
  'Wiederholung':         { icon: Calendar,    description: 'Wiederholungsoptionen für Anfragen.' },
  'Wochenende':  { icon: Calendar,    description: 'Umgang mit Wochenenden.' },
  'Pakete':               { icon: Package,     description: 'Zusammenfassen von Lektionen zu Paketen — unabhängig von der späteren Lehrer-Kontaktierung.' },
  'Status':               { icon: ShieldCheck, description: 'Übersicht der aktiven Systeme und Feature-Flags.' },
  'Zeiteinstellungen — Antwortzeit pro Lehrperson': { icon: Clock, description: 'Wie lange jede Lehrperson Zeit hat zu antworten.' },
  'Zeiteinstellungen — Vorlauf & Kontaktierung':    { icon: Calendar, description: 'Wann darf eine Anfrage gestellt werden.' },
  'Zeiteinstellungen — Antwort-Links & Fristen':    { icon: LinkIcon, description: 'Gültigkeit von Antwort-Links und Fristen.' },
  'Zeiteinstellungen — Auto-Stornierung':           { icon: ShieldCheck, description: 'Sicherheitsnetz für offene Anfragen.' },
  'Zeiteinstellungen — Storno-Bestätigung':         { icon: Clock, description: 'Frist, um ein Storno zu bestätigen oder abzulehnen.' },
  'Kalender — Export & Feed': { icon: Calendar, description: 'Welche Ereignisse und Details in deinem abonnierten Kalender-Feed erscheinen.' },
}

function subgroupIcon(subgroup: SettingSubgroup) {
  const meta = SUBGROUP_META[subgroup.name]
  if (meta) return meta.icon
  // Generated multi-type subgroups: colored by type of widget content
  const types = new Set(subgroup.keys.map(k => k.includes(':') ? k.split(':')[0] : 'substitution_request'))
  if (types.size === 1) {
    const ct = [...types][0]
    if (ct === 'panic_zone') return AlertTriangle
    if (ct === 'calendar') return Calendar
  }
  return Settings
}

export function BoolBadge({ value }: { value: boolean }) {
  return value
    ? <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded text-xs font-medium"><Check className="w-3 h-3" />Ja</span>
    : <span className="inline-flex items-center gap-1 text-gray-500 bg-gray-100 px-2 py-0.5 rounded text-xs font-medium"><X className="w-3 h-3" />Nein</span>
}

function SettingRow({ label, description, warning, hint, isOverridden, isReadonly, children }: {
  label: string
  description?: string
  warning?: string | null
  hint?: ReactNode
  isOverridden?: boolean
  isReadonly?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex-1 min-w-0 pr-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-800">{label}</span>
          {isReadonly && (
            <span className="inline-flex items-center gap-0.5 text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              <Eye className="w-3 h-3" /> Read-only
            </span>
          )}
          {isOverridden && !isReadonly && (
            <span className="inline-flex items-center gap-0.5 text-xs text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
              <Sparkles className="w-3 h-3" /> Personalisiert
            </span>
          )}
          {warning && isOverridden && (
            <GlassTooltip content={warning}>
              <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />
            </GlassTooltip>
          )}
        </div>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
        {hint}
      </div>
      <div className="flex-shrink-0 flex items-center gap-2">
        {children}
      </div>
    </div>
  )
}

function SectionHeader({ icon: Icon, title, description }: { icon: typeof Settings; title: string; description: string }) {
  return (
    <>
      <div className="flex items-center gap-3 mb-1">
        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
          <Icon className="w-4 h-4 text-indigo-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">{description}</p>
    </>
  )
}

function SubgroupCard({ subgroup, children }: { subgroup: SettingSubgroup; children: React.ReactNode }) {
  const meta = SUBGROUP_META[subgroup.name] || { icon: subgroupIcon(subgroup), description: '' }
  const Icon = meta.icon
  const description = meta.description || subgroup.intro || subgroup.leitfrage || ''

  return (
    <div data-subgroup={subgroup.name} className="bg-white rounded-lg border p-6 mb-6">
      <SectionHeader icon={Icon} title={subgroup.name} description={description} />
      <div>{children}</div>
    </div>
  )
}

function DependencyGroup({ parentLabel, parentEnabled, children }: {
  parentLabel: string
  parentEnabled: boolean
  children: React.ReactNode
}) {
  return (
    <div className="mt-4">
      <div className="pl-6 border-l-2 border-indigo-100 bg-indigo-50/30 rounded-r-lg p-4">
        {!parentEnabled && (
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3 bg-yellow-50 border border-yellow-200 rounded p-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            <span>Diese Einstellungen gelten nur wenn <strong>{parentLabel}</strong> aktiviert ist.</span>
          </div>
        )}
        <div className={parentEnabled ? '' : 'opacity-50 pointer-events-none'}>
          {children}
        </div>
      </div>
    </div>
  )
}

function CategoryTabs({ categories, activeCategory, onSelect }: {
  categories: SettingCategory[]
  activeCategory: string
  onSelect: (name: string) => void
}) {
  return (
    <div className="flex gap-1 border-b border-gray-200">
      {categories.map(cat => {
        const meta = CATEGORY_META[cat.name] || { icon: Settings }
        const Icon = meta.icon
        const isActive = cat.name === activeCategory
        return (
          <button
            key={cat.name}
            onClick={() => onSelect(cat.name)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition border-b-2 -mb-px ${
              isActive
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {cat.name}
          </button>
        )
      })}
    </div>
  )
}

interface TeacherSettingsViewProps {
  data: TeacherSettingsResponse
  /** false = read-only preview (superadmin), true = teacher live view */
  editable: boolean
  overrides: Record<string, unknown>
  validationErrors?: Record<string, string>
  onUpdateOverride?: (key: string, value: unknown) => void
  onResetKey?: (key: string) => void
  /** Controlled mode (TeacherSettingsPage hash-sync). Omit for uncontrolled. */
  activeCategory?: string
  activeSubgroup?: string
  onCategoryChange?: (name: string) => void
  onSubgroupChange?: (name: string) => void
}

/**
 * Shared teacher-settings render tree.
 *
 * Used read-write by TeacherSettingsPage (real teacher) and read-only by
 * TeacherPreviewModal (superadmin preview) — both render the exact same data
 * pipeline so the preview cannot drift from reality.
 */
export function TeacherSettingsView({
  data,
  editable,
  overrides,
  validationErrors = {},
  onUpdateOverride,
  onResetKey,
  activeCategory: activeCategoryProp,
  activeSubgroup: activeSubgroupProp,
  onCategoryChange,
  onSubgroupChange,
}: TeacherSettingsViewProps) {
  const [internalCategory, setInternalCategory] = useState<string>(() => data.categories[0]?.name || '')
  const [internalSubgroup, setInternalSubgroup] = useState<string>('')
  // Controlled mode is decided by the presence of the change handler so that
  // passing an initially-undefined preselected category (preview modal) keeps
  // the component controlled throughout.
  const isControlled = onCategoryChange !== undefined
  const activeCategory = (isControlled ? activeCategoryProp ?? internalCategory : internalCategory) || ''
  const activeSubgroup = isControlled ? (activeSubgroupProp ?? '') : internalSubgroup

  const {
    school_defaults, effective_config, effective_by_type, school_defaults_by_type,
    editable_keys, visible_keys, definitions, categories, readonly_keys,
  } = data
  const editableSet = useMemo(() => new Set(editable_keys), [editable_keys])
  const visibleSet = useMemo(() => new Set(visible_keys), [visible_keys])
  const readonlySet = useMemo(() => new Set(readonly_keys), [readonly_keys])

  // Client-side resilient pruning (server already drops empty subgroups/tabs)
  const categoriesVisible = useMemo(() => {
    return categories
      .map(cat => ({
        ...cat,
        subgroups: cat.subgroups.filter(sg =>
          sg.keys.some(k => visibleSet.has(k))
        ),
      }))
      .filter(cat => cat.subgroups.length > 0)
  }, [categories, visibleSet])

  const currentCategory = categoriesVisible.find(c => c.name === activeCategory) || categoriesVisible[0]

  const definitionsByKey = useMemo(() => {
    const map: Record<string, TeacherSettingDefinition> = {}
    for (const d of definitions) map[d.key] = d
    return map
  }, [definitions])

  const getEffectiveValue = (storageKey: string): unknown => {
    const { ct, key } = splitKey(storageKey)
    if (ct) {
      return effective_by_type?.[ct]?.[key] ?? school_defaults_by_type?.[ct]?.[key] ?? ''
    }
    const val = (effective_config as unknown as Record<string, unknown>)[key]
      ?? (school_defaults as unknown as Record<string, unknown>)[key]
    return val === null || val === undefined ? '' : val
  }

  const isOverridden = (storageKey: string) => storageKey in overrides

  const renderField = (storageKey: string, disabled: boolean = false) => {
    const defn = definitionsByKey[storageKey]
    if (!defn) return null

    const canEdit = editable && editableSet.has(storageKey) && !disabled && !defn.readonly
    const overridden = isOverridden(storageKey)
    const value = overridden ? overrides[storageKey] : getEffectiveValue(storageKey)
    const err = validationErrors[storageKey]

    // ---------- read-only rendering ----------
    if (!canEdit) {
      if (defn.type === 'bool') {
        return (
          <div className="flex items-center gap-2">
            <BoolBadge value={Boolean(value)} />
            {!value && storageKey === 'priority_system_enabled' && (
              <DisabledTooltip reason="Prioritätssystem ist deaktiviert" action="Kontaktieren Sie den Administrator" />
            )}
          </div>
        )
      }
      if ((defn.type === 'string' || defn.type === 'str') && defn.choices) {
        return <span className="capitalize text-xs bg-gray-100 px-2 py-0.5 rounded font-mono">{choiceLabel(displayValue(value))}</span>
      }
      if (defn.type === 'json_list' && defn.choices) {
        const listValue = Array.isArray(value) ? value : (defn.default_value as string[])
        const isModeOrder = storageKey === 'default_mode_priority_order'
        const modeLabels = isModeOrder ? MODE_ORDER_LABELS : LEGACY_MODE_LABELS
        const shown = isModeOrder ? listValue.filter((m: string) => MODE_ORDER_ALLOWED.has(m)) : listValue
        return (
          <span className="text-xs text-gray-600">
            {shown.map((m: string, i: number) => `${i + 1}. ${modeLabels[m] || m}`).join(' → ')}
          </span>
        )
      }
      if (defn.type === 'color') {
        return (
          <span className="inline-flex items-center gap-2 text-xs text-gray-600">
            <span className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: String(value || '#000000') }} />
            {String(value || '—')}
          </span>
        )
      }
      if (Array.isArray(value)) return <span className="text-sm text-gray-600">{formatJsonListDisplay(value)}</span>
      return <span className="text-sm text-gray-700">{displayValue(value)}</span>
    }

    // ---------- editable rendering ----------
    const resetBtn = overridden && onResetKey ? (
      <button onClick={() => onResetKey(storageKey)} className="text-xs text-gray-400 hover:text-gray-600" title="Zuruecksetzen">
        <RotateCcw className="w-3 h-3" />
      </button>
    ) : null

    if (defn.type === 'bool') {
      return (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onUpdateOverride?.(storageKey, !value)}
            role="switch"
            aria-checked={Boolean(value)}
            aria-label={defn.label}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
              value ? 'bg-indigo-600' : 'bg-gray-300'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
          {resetBtn}
        </div>
      )
    }

    if (defn.type === 'int') {
      const numVal = typeof value === 'number' ? value : Number(value) || 0
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={numVal}
            onChange={e => onUpdateOverride?.(storageKey, Number(e.target.value))}
            className={`w-20 px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
              err ? 'border-red-300 bg-red-50' : overridden ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300'
            }`}
            min={defn.min_value ?? undefined}
            max={defn.max_value ?? undefined}
          />
          {err && <span className="text-xs text-red-600">{err}</span>}
          {resetBtn}
        </div>
      )
    }

    if (defn.type === 'float') {
      return (
        <div className="flex items-center gap-2">
          <FloatInput
            value={typeof value === 'number' ? value : null}
            onChange={v => onUpdateOverride?.(storageKey, v ?? 0)}
            placeholder="z.B. 0.25"
            className={`w-20 px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
              err ? 'border-red-300 bg-red-50' : overridden ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300'
            }`}
          />
          {err && <span className="text-xs text-red-600">{err}</span>}
          {resetBtn}
        </div>
      )
    }

    if ((defn.type === 'string' || defn.type === 'str') && defn.choices) {
      return (
        <div className="flex items-center gap-2">
          <select
            value={displayValue(value, '')}
            onChange={e => onUpdateOverride?.(storageKey, e.target.value)}
            className={`px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
              overridden ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300'
            }`}
          >
            {defn.choices.map(c => (
              <option key={c} value={c}>{choiceLabel(c)}</option>
            ))}
          </select>
          {resetBtn}
        </div>
      )
    }

    if (defn.type === 'str' || defn.type === 'string') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={displayValue(value, '')}
            onChange={e => onUpdateOverride?.(storageKey, e.target.value)}
            className={`w-56 px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
              overridden ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300'
            }`}
          />
          {resetBtn}
        </div>
      )
    }

    if (defn.type === 'json_list' && defn.choices && storageKey === 'default_mode_priority_order') {
      const listValue = (Array.isArray(value) ? value : (defn.default_value as string[])).filter(m => MODE_ORDER_ALLOWED.has(m))
      const choices = defn.choices.filter(c => MODE_ORDER_ALLOWED.has(c))
      return (
        <div className="flex items-center gap-2 flex-wrap">
          {[0, 1].map(idx => (
            <div key={idx} className="flex items-center gap-1">
              <span className="text-xs text-gray-400">{idx + 1}.</span>
              <select
                value={listValue[idx] || ''}
                onChange={e => {
                  const newOrder = [...listValue]
                  newOrder[idx] = e.target.value
                  onUpdateOverride?.(storageKey, newOrder.filter(m => MODE_ORDER_ALLOWED.has(m)))
                }}
                className={`px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                  overridden ? 'border-indigo-300 bg-indigo-50' : 'border-gray-300'
                }`}
              >
                {choices.map(c => (
                  <option key={c} value={c}>{MODE_ORDER_LABELS[c] || c}</option>
                ))}
              </select>
            </div>
          ))}
          {resetBtn}
        </div>
      )
    }

    if (defn.type === 'json_list') {
      const listValue = Array.isArray(value) ? value : (defn.default_value as (string | number)[]) || []
      return (
        <div className="flex items-center gap-2 w-full max-w-md">
          <TagInput
            value={listValue as (string | number)[]}
            onChange={v => onUpdateOverride?.(storageKey, v)}
            placeholder="Wert hinzufuegen..."
          />
          {resetBtn}
        </div>
      )
    }

    if (defn.type === 'color') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={String(value || '#000000')}
            onChange={e => onUpdateOverride?.(storageKey, e.target.value)}
            className="w-16 h-9 border border-gray-300 rounded cursor-pointer"
          />
          {resetBtn}
        </div>
      )
    }

    return <span className="text-sm text-gray-700">{displayValue(value)}</span>
  }

  const renderSettingRow = (storageKey: string, disabled: boolean = false) => {
    const defn = definitionsByKey[storageKey]
    if (!defn) return null
    if (!visibleSet.has(storageKey)) return null

    const isReadonly = readonlySet.has(storageKey) || defn.readonly === true
    const overridden = isOverridden(storageKey)

    if (storageKey === 'max_pending_requests_per_teacher') {
      const pending = (effective_config as unknown as Record<string, unknown>).current_pending_requests
      return (
        <SettingRow key={storageKey} label={defn.label} description={defn.description} warning={defn.warning} isOverridden={overridden} isReadonly={isReadonly}>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">
              <span className={(pending != null && Number(pending) >= Number(getEffectiveValue(storageKey))) ? 'text-red-600' : ''}>
                {String(pending ?? '?')} offen
              </span>
            </span>
            {renderField(storageKey, disabled)}
          </div>
        </SettingRow>
      )
    }

    const currentValue = overridden ? overrides[storageKey] : getEffectiveValue(storageKey)
    const hideBookingsHint = storageKey === 'radar:dashboard_upcoming_bookings_limit' && Number(currentValue) === 0
      ? (
        <div className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 text-amber-500 mt-0.5" />
          <span>Bei 0 wird die Rubrik «Meine Vertretungen» auf Ihrem Dashboard vollständig ausgeblendet.</span>
        </div>
      )
      : undefined

    return (
      <SettingRow key={storageKey} label={defn.label} description={defn.description} warning={defn.warning} hint={hideBookingsHint} isOverridden={overridden} isReadonly={isReadonly}>
        {renderField(storageKey, disabled)}
      </SettingRow>
    )
  }

  /** Keys of a subgroup that will actually render (visible + defined). */
  const renderableKeys = (subgroup: SettingSubgroup): string[] =>
    subgroup.keys.filter(k => visibleSet.has(k) && definitionsByKey[k])

  const renderSubgroup = (subgroup: SettingSubgroup) => {
    const renderable = renderableKeys(subgroup)
    if (renderable.length === 0) return null

    // Dependency-grouped subgroups: parent toggle + dependent children
    if (subgroup.name === 'Affinity-Ranking' || subgroup.name === 'Multi-Week-Pakete') {
      const parentKey = subgroup.name === 'Affinity-Ranking' ? 'affinity_bonus_enabled' : 'multi_week_packages_enabled'
      // Per-field delegation: parent may be hidden while children are
      // visible/editable — never drop the whole card in that case, render
      // the visible children flat instead.
      if (visibleSet.has(parentKey)) {
        const parentEnabled = Boolean(getEffectiveValue(parentKey))
        const parentDefn = definitionsByKey[parentKey]
        const childKeys = renderable.filter(k => k !== parentKey)

        return (
          <SubgroupCard key={subgroup.name} subgroup={subgroup}>
            {renderSettingRow(parentKey)}
            {childKeys.length > 0 && (
              <DependencyGroup parentLabel={parentDefn?.label || parentKey} parentEnabled={parentEnabled}>
                {childKeys.map(key => renderSettingRow(key, !parentEnabled))}
              </DependencyGroup>
            )}
          </SubgroupCard>
        )
      }
      const childKeys = renderable.filter(k => k !== parentKey)
      if (childKeys.length === 0) return null
      return (
        <SubgroupCard key={subgroup.name} subgroup={subgroup}>
          {childKeys.map(key => renderSettingRow(key))}
        </SubgroupCard>
      )
    }

    return (
      <SubgroupCard key={subgroup.name} subgroup={subgroup}>
        {renderable.map(key => renderSettingRow(key))}
        {editable && subgroup.name === 'Kalender — Export & Feed' && renderFeedPreview()}
      </SubgroupCard>
    )
  }

  const renderFeedPreview = () => {
    // "Kalender — Export & Feed" (calendar-feed-v1): Live-Vorschau des ICS-Feeds
    // unterhalb der Einstellungszeilen. Nutzt die aktuell effektiven Werte
    // (persönlich > Schul-Default) inkl. ungespeicherter Overrides.
    const get = (key: string): unknown => {
      const storageKey = `calendar:${key}`
      if (storageKey in overrides) return overrides[storageKey]
      return getEffectiveValue(storageKey)
    }
    const bool = (key: string, fallback: boolean): boolean => {
      const v = get(key)
      return typeof v === 'boolean' ? v : fallback
    }
    return (
      <CalendarFeedPreview
        settings={{
          feed_include_substitutions: bool('feed_include_substitutions', true),
          feed_include_absences: bool('feed_include_absences', true),
          feed_include_cancelled: bool('feed_include_cancelled', true),
          feed_include_open_requests: bool('feed_include_open_requests', false),
          feed_show_subject: bool('feed_show_subject', true),
          feed_show_klass: bool('feed_show_klass', true),
          feed_show_room: bool('feed_show_room', true),
          feed_show_teacher: bool('feed_show_teacher', true),
        }}
      />
    )
  }

  if (categoriesVisible.length === 0) {
    return (
      <div className="p-10 text-center">
        <Settings className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">Keine Einstellungen freigegeben</p>
        <p className="text-sm text-gray-400 mt-1">
          Derzeit sind alle Schuleinstellungen für Lehrpersonen verborgen. Kontaktieren Sie die Administration, wenn Sie eine Einstellung benötigen.
        </p>
      </div>
    )
  }

  const subgroupsToRender = currentCategory?.subgroups || []

  return (
    <div className="flex flex-col min-h-0">
      <div className="border-b bg-white overflow-x-auto">
        <CategoryTabs
          categories={categoriesVisible}
          activeCategory={currentCategory?.name || ''}
          onSelect={(name) => {
            if (isControlled) {
              onCategoryChange?.(name)
            } else {
              setInternalCategory(name)
              setInternalSubgroup('')
            }
          }}
        />
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="w-56 border-r bg-gray-50/50 p-3 space-y-0.5 flex-shrink-0 overflow-y-auto hidden lg:block">
          {subgroupsToRender.map(sg => {
            const Icon = subgroupIcon(sg)
            const isActive = (activeSubgroup || subgroupsToRender[0]?.name) === sg.name
            const hasOverrides = editable && sg.keys.some(k => k in overrides && visibleSet.has(k))
            return (
              <button
                key={sg.name}
                onClick={() => {
                  if (isControlled) {
                    onSubgroupChange?.(sg.name)
                  } else {
                    setInternalSubgroup(sg.name)
                  }
                  document.querySelector(`[data-subgroup="${CSS.escape(sg.name)}"]`)?.scrollIntoView({ behavior: 'smooth' })
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition text-left ${
                  isActive ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 truncate">{sg.name}</span>
                {hasOverrides && <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />}
              </button>
            )
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl">
            {subgroupsToRender.map(sg => renderSubgroup(sg))}
          </div>
        </div>
      </div>
    </div>
  )
}
